import { auth, db } from "../firebase";
import { doc, setDoc, serverTimestamp, onSnapshot, getDoc } from "firebase/firestore";
import { genProposalServer } from "./functions";

export async function ensureAnon() {
  const { signInAnonymously } = await import("firebase/auth");
  if (!auth.currentUser) await signInAnonymously(auth);
  return auth.currentUser!;
}

export async function createAndGenerateProposal(idea: string) {
  const user = await ensureAnon();

  const id = (crypto as any).randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  const ref = doc(db, "proposals", id);

  await setDoc(ref, {
    proposalId: id,
    ownerUid: user.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    status: "queued",
    inputs: { idea }
  });

  await genProposalServer(id, { idea });
  await addProposalToUserProfile(id, user.uid);

  return id;
}

export function watchProposal(
  proposalId: string,
  onData: (p: any) => void,
  onError?: (e: any) => void
) {
  const ref = doc(db, "proposals", proposalId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) onData({ id: snap.id, ...snap.data() });
  }, onError);
}

export async function fetchProposalOnce(proposalId: string) {
  const snap = await getDoc(doc(db, "proposals", proposalId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Parse a string value for a given proposal section key into the correct type for Firestore
export function parseProposalSectionValue(sectionKey: keyof import("../types").ProposalOutput, editValue: string): any {
  let value: any = editValue;
  switch (sectionKey) {
    case "keyFeatures":
      value = editValue.split('\n').map(line => {
        const match = line.match(/^\[(MVP|Next)\] ([^:]+): (.+)$/);
        return match ? { priority: match[1], title: match[2], desc: match[3] } : null;
      }).filter(Boolean);
      break;
    case "techStack":
    case "risksMitigations":
    case "acceptanceCriteria":
      value = editValue.split(/,|\n/).map(s => s.trim()).filter(Boolean);
      break;
    case "deliveryPlan":
      // Not implemented: would need a more complex parser
      break;
    case "pricingPackages":
      // Not implemented: would need a more complex parser
      break;
  }
  return value;
}
async function addProposalToUserProfile(proposalId: string, userUid: string) {
  const userIdeasRef = doc(db, "userProfiles", userUid);
  const userIdeasSnap = await getDoc(userIdeasRef);
  let ideas: string[] = [];
  if (userIdeasSnap.exists()) {
    const data = userIdeasSnap.data();
    ideas = data.ideas || [];
  }
  if (!ideas.includes(proposalId)) {
    const newIdeas = [...ideas, proposalId];
    await setDoc(userIdeasRef, { ideas: newIdeas }, { merge: true });
  }
}

export async function getUserProposalIds(userUid: string): Promise<string[]> {
  const userIdeasRef = doc(db, "userProfiles", userUid);
  const userIdeasSnap = await getDoc(userIdeasRef);
  let ideas: string[] = [];
  if (userIdeasSnap.exists()) {
    const data = userIdeasSnap.data();
    ideas = data.ideas || [];
  }
  return ideas;
}